import type { AiConfig, Insight } from '@regia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '../../lib/api';
import { keys } from '../../lib/query';

export function useAiConfig() {
  return useQuery({ queryKey: ['ai-config'], queryFn: () => http.get<AiConfig>('/api/ai/config'), staleTime: 60_000 });
}

/** true quando l'AI è pronta (chiave presente, accesa, tetto non raggiunto). */
export function aiReady(c: AiConfig | undefined): boolean {
  if (!c?.enabled) return false;
  if (c.provider === 'claude-cli') return c.hasClaudeLogin;
  const hasKey = c.provider === 'anthropic' ? c.hasAnthropicKey : c.hasDeepseekKey;
  return hasKey && c.monthCostEur < c.monthlyBudgetEur;
}

export function useInsights(pageId: string) {
  return useQuery({ queryKey: ['insights', pageId], queryFn: () => http.get<Insight[]>(`/api/insights?pageId=${pageId}`) });
}

export function useAnalyze(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<Insight>(`/api/insights/page/${pageId}/analyze`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insights', pageId] });
      qc.invalidateQueries({ queryKey: ['ai-config'] });
    },
  });
}

export function useDraftWeek(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<{ created: number }>(`/api/insights/page/${pageId}/drafts`),
    onSuccess: () => {
      for (const k of [['posts'], ['cadence'], ['ai-config'], keys.home]) qc.invalidateQueries({ queryKey: k });
    },
  });
}

export function useDraftReplies(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.post<{ drafted: number }>(`/api/insights/page/${pageId}/replies`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reviews', pageId] });
      qc.invalidateQueries({ queryKey: ['ai-config'] });
    },
  });
}

export function useActionToTask(pageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, index }: { id: string; index: number }) => http.post<Insight>(`/api/insights/${id}/actions/${index}/task`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insights', pageId] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: keys.home });
    },
  });
}
